import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DealActivityKind } from '@bhops/shared';
import { dealsApi, type CreateDealActivityBody } from '@/api/deals';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input, Select, Textarea } from '@/components/Form';
import { useToast } from '@/components/Toast';

// Option labels are intentionally text-only — an emoji embedded inside an
// `<option>` element cannot be `aria-hidden`, so screen readers would announce
// it ("page facing up 제안서 발송"). Timeline rows in RobotDetailPage /
// DeploymentsPage own their own icon-label maps separately for visual flair.
const KIND_LABEL: Record<DealActivityKind, string> = {
  CALL: '통화',
  MEETING: '미팅',
  EMAIL: '이메일',
  PROPOSAL_SENT: '제안서 발송',
  CUSTOM: '기타',
};

interface Props {
  open: boolean;
  onClose: () => void;
  dealId: string;
  dealHumanId: string;
}

export function DealActivityCreateModal({ open, onClose, dealId, dealHumanId }: Props) {
  const toast = useToast();
  const qc = useQueryClient();

  const [kind, setKind] = useState<DealActivityKind>(DealActivityKind.CALL);
  // Default to "now" so operators recording an activity right after
  // the call don't have to type the date.
  const [occurredAt, setOccurredAt] = useState(() => toLocalDateTimeInput(new Date()));
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: CreateDealActivityBody) => dealsApi.createActivity(dealId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals', dealId] });
      qc.invalidateQueries({ queryKey: ['deals'] }); // _count.activities updates
      toast.success(`${dealHumanId}에 활동 기록을 추가했습니다.`, '활동 기록');
      onClose();
      setKind(DealActivityKind.CALL);
      setOccurredAt(toLocalDateTimeInput(new Date()));
      setNotes('');
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    if (!notes.trim()) {
      setError(
        '내용을 입력해 주세요. 내용이 있는 활동 기록이 있어야 딜을 제안에서 협상 단계로 넘길 수 있습니다.',
      );
      return;
    }
    create.mutate({
      kind,
      occurredAt: new Date(occurredAt).toISOString(),
      notes,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="활동 기록 추가"
      width={460}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button type="submit" form="deal-activity-form" disabled={create.isPending}>
            {create.isPending ? '저장 중…' : '기록'}
          </Button>
        </>
      }
    >
      <form id="deal-activity-form" onSubmit={submit} noValidate>
        <FormError>{error}</FormError>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="활동 유형" htmlFor="a-kind" required>
            <Select
              id="a-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as DealActivityKind)}
            >
              {Object.entries(KIND_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="발생 시각" htmlFor="a-when" required>
            <Input
              id="a-when"
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              required
            />
          </Field>
        </div>

        <Field label="내용" htmlFor="a-notes" required hint="내용이 비어 있으면 기록할 수 없습니다">
          <Textarea
            id="a-notes"
            rows={5}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="고객사 반응, 다음 액션, 팔로업 일정 등"
            required
          />
        </Field>
      </form>
    </Modal>
  );
}

/**
 * Converts a Date to a `YYYY-MM-DDTHH:mm` string suitable for an
 * `<input type="datetime-local">`. Strips seconds + timezone so
 * the picker doesn't reject the value.
 */
function toLocalDateTimeInput(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
